import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Prompt } from "react-router-dom";
import moment from "moment";
import Admin from "../layout/Admin";
import spinner from "../layout/spinner.gif";
import {
	getProduct,
	changePrice,
	getAlerts,
	saveAlerts,
	testAlert,
	getAds,
	saveAds,
} from "../../actions/settings";
import Prices from "./Prices";
import AlertCard from "./AlertCard";
import AdSettings from "./AdSettings";
import { ALERTS, PAPER_LOW_MAX, REAR_TRAY_LOW_MAX } from "./alertLabels";
import { primaryButton, secondaryButton } from "../template/templateLabels";

// Prices, and the mails the backend sends the staff about the kiosks: who gets each
// and how few sheets left brings the low paper ones (its utils/alerts.js), and the
// ads page of cloudprint.mn. Prices are saved one at a time; the mails and the paper
// numbers are one form, saved together; the ads page is a form of its own.

const card = "bg-white rounded-lg shadow-sm dark:bg-gray-800";

const Section = ({ icon, title, text, aside, first, children }) => (
	<section
		className={
			"grid gap-4 py-8 lg:grid-cols-3 lg:gap-8" +
			(first ? "" : " border-t border-gray-200 dark:border-gray-700")
		}
	>
		<div>
			<div className="flex items-center gap-3">
				<span
					className="flex items-center justify-center flex-shrink-0 text-purple-600 bg-purple-100 rounded-lg w-9 h-9 dark:text-purple-200 dark:bg-purple-900"
					aria-hidden="true"
				>
					<i className={"fas " + icon} />
				</span>
				<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">{title}</h3>
			</div>
			<p className="mt-3 text-sm text-gray-500 dark:text-gray-400">{text}</p>
			{aside}
		</div>
		<div className="min-w-0 lg:col-span-2">{children}</div>
	</section>
);

const Loading = () => (
	<img
		src={spinner}
		className="my-6"
		style={{ width: "50px", margin: "auto", display: "block" }}
		alt="Loading..."
	/>
);

// The form as the page edits it: the numbers as the fields hold them
const formOf = (alerts) => ({
	mails: Object.fromEntries(
		ALERTS.map(({ key }) => [key, alerts.mails[key] || { enabled: true, to: [] }]),
	),
	paperLow: String(alerts.paperLow),
	rearTrayLow: String(alerts.rearTrayLow),
});

// A field's whole number from 0 to max, or null
const countOf = (value, max) => {
	const text = String(value).trim();
	const number = Number(text);
	return text !== "" && Number.isInteger(number) && number >= 0 && number <= max ? number : null;
};

const CountField = ({ label, value, max, onChange, help }) => {
	const bad = countOf(value, max) === null;
	return (
		<label className="block">
			<span className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
				{label}
			</span>
			<span className="relative block">
				<input
					type="number"
					min="0"
					max={max}
					step="1"
					inputMode="numeric"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					className={
						"block w-full h-10 pl-3 pr-20 text-sm text-gray-700 tabular-nums bg-white border rounded-lg dark:text-gray-300 dark:bg-gray-700 focus:border-purple-400 focus:ring focus:ring-purple-200 focus:ring-opacity-50 focus:outline-none dark:focus:ring-gray-500 " +
						(bad ? "border-red-400 dark:border-red-500" : "border-gray-300 dark:border-gray-600")
					}
				/>
				<span className="absolute inset-y-0 flex items-center text-sm text-gray-400 pointer-events-none right-3">
					ширхэг
				</span>
			</span>
			<span
				className={
					"block mt-1 text-xs " +
					(bad ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-gray-400")
				}
			>
				{bad ? `0-${max} хүртэлх бүхэл тоо оруулна уу` : help}
			</span>
		</label>
	);
};

const Settings = ({
	getProduct,
	changePrice,
	getAlerts,
	saveAlerts,
	testAlert,
	getAds,
	saveAds,
	settings: { product, loading, alerts, admins, alertsLoading },
}) => {
	const [form, setForm] = useState(null);
	const [saving, setSaving] = useState(false);
	const [testing, setTesting] = useState(null);
	// Whether the ads form has changes it has not saved
	const [adsDirty, setAdsDirty] = useState(false);

	useEffect(() => {
		getProduct();
		getAlerts();
	}, [getProduct, getAlerts]);

	// What was loaded or saved, which the backend has cleaned up
	useEffect(() => {
		if (alerts) setForm(formOf(alerts));
	}, [alerts]);

	const saved = useMemo(() => (alerts ? formOf(alerts) : null), [alerts]);
	const dirty = Boolean(form && saved && JSON.stringify(form) !== JSON.stringify(saved));

	const paperLow = form ? countOf(form.paperLow, PAPER_LOW_MAX) : null;
	const rearTrayLow = form ? countOf(form.rearTrayLow, REAR_TRAY_LOW_MAX) : null;
	// A mail that is on with nobody would look on while nobody gets it
	const emptyMails = form
		? ALERTS.filter(({ key }) => form.mails[key].enabled && form.mails[key].to.length === 0)
		: [];
	const canSave =
		dirty && !saving && paperLow !== null && rearTrayLow !== null && emptyMails.length === 0;

	// Reloading or closing the tab would lose what is not saved
	const unsaved = dirty || adsDirty;
	useEffect(() => {
		if (!unsaved) return undefined;
		const warn = (e) => {
			e.preventDefault();
			e.returnValue = "";
		};
		window.addEventListener("beforeunload", warn);
		return () => window.removeEventListener("beforeunload", warn);
	}, [unsaved]);

	const setMail = (key, mail) =>
		setForm((current) => ({ ...current, mails: { ...current.mails, [key]: mail } }));

	const save = async () => {
		if (!canSave) return;
		setSaving(true);
		await saveAlerts({ mails: form.mails, paperLow, rearTrayLow });
		setSaving(false);
	};

	// A test goes to the saved list, so it waits for the form to be saved
	const testStateOf = (key) => {
		const mail = alerts.mails[key];
		if (dirty) return { can: false, reason: "Эхлээд хадгална уу" };
		if (!mail || !mail.enabled || mail.to.length === 0) return { can: false, reason: null };
		return { can: true, busy: testing === key, reason: null };
	};

	const test = async (key) => {
		setTesting(key);
		await testAlert(key);
		setTesting(null);
	};

	const savedBy =
		alerts && alerts.updatedAt ? (
			<p className="flex items-start mt-4 text-xs text-gray-500 dark:text-gray-400">
				<i className="mt-0.5 mr-2 fas fa-history" />
				<span>
					Сүүлд {alerts.updatedBy ? alerts.updatedBy + " " : ""}
					{moment(alerts.updatedAt).format("YYYY-MM-DD HH:mm")}-д хадгалсан
				</span>
			</p>
		) : null;

	const alertsFailed = !alertsLoading && !alerts;

	return (
		<Admin>
			<Prompt when={unsaved} message="Хадгалаагүй өөрчлөлт байна. Хуудаснаас гарах уу?" />
			<div className="min-w-0">
				<div className="my-6">
					<h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-200">Тохиргоо</h2>
					<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
						Үнэ, цаасны сануулга, ажилтнууд руу очих мэйл, реклам
					</p>
				</div>

				<Section
					first
					icon="fa-tags"
					title="Үнэ"
					text="Киоск дээр төлөх үнэ. Мөр бүрийг тусад нь хадгална."
				>
					{loading ? <Loading /> : <Prices product={product} changePrice={changePrice} />}
				</Section>

				{alertsFailed ? (
					<div className={"flex flex-wrap items-center justify-between gap-4 p-5 mb-8 " + card}>
						<p className="text-sm text-gray-600 dark:text-gray-300">
							<i className="mr-2 text-red-500 fas fa-exclamation-circle" />
							Мэйлийн тохиргоог ачаалж чадсангүй
						</p>
						<button type="button" className={secondaryButton} onClick={getAlerts}>
							Дахин оролдох
						</button>
					</div>
				) : (
					<>
						<Section
							icon="fa-layer-group"
							title="Цаасны сануулга"
							text="Киоскийн цаас энэ тоо ба түүнээс цөөн болоход доорх мэйл явна."
						>
							{!form ? (
								<Loading />
							) : (
								<div className={"grid gap-6 p-5 sm:grid-cols-2 " + card}>
									<CountField
										label="A4 цаас"
										value={form.paperLow}
										max={PAPER_LOW_MAX}
										onChange={(value) => setForm({ ...form, paperLow: value })}
										help="Киоск хуудсанд Low paper гэж мөн харагдана."
									/>
									<CountField
										label="Rear tray-ийн цаас (rear tray)"
										value={form.rearTrayLow}
										max={REAR_TRAY_LOW_MAX}
										onChange={(value) => setForm({ ...form, rearTrayLow: value })}
										help="Энэ тоонд хүрэхэд нэг удаа мэйл явна."
									/>
								</div>
							)}
						</Section>

						<Section
							icon="fa-envelope"
							title="Мэйл мэдэгдэл"
							text="Киоскийн асуудлын талаар хэн мэйл авахыг мэйл бүрд сонгоно. Админ бус хаяг ч нэмж болно. Унтраасан мэйл хэнд ч явахгүй."
							aside={savedBy}
						>
							{!form ? (
								<Loading />
							) : (
								<div className="space-y-4">
									{ALERTS.map((alert) => (
										<AlertCard
											key={alert.key}
											alert={alert}
											mail={form.mails[alert.key]}
											admins={admins}
											onChange={(mail) => setMail(alert.key, mail)}
											testState={testStateOf(alert.key)}
											onTest={() => test(alert.key)}
										/>
									))}
								</div>
							)}
						</Section>
					</>
				)}

				<Section
					icon="fa-bullhorn"
					title="Киоскын реклам"
					text="Киоскын дэлгэц дээр реклам байршуулахыг хүссэн байгууллагуудад cloudprint.mn дээр харагдах мэдээлэл."
				>
					<AdSettings getAds={getAds} saveAds={saveAds} onDirty={setAdsDirty} />
				</Section>

				{dirty && (
					<div className="sticky bottom-0 z-10 pb-4">
						<div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 bg-white border border-purple-200 rounded-lg shadow-lg dark:bg-gray-800 dark:border-purple-800">
							<p className="text-sm text-gray-600 dark:text-gray-300">
								{emptyMails.length > 0 ? (
									<span className="text-red-600 dark:text-red-400">
										<i className="mr-2 fas fa-exclamation-circle" />
										{emptyMails.map((alert) => "«" + alert.name + "»").join(", ")}: хүлээн авагч
										нэмэх эсвэл унтраана уу
									</span>
								) : (
									<>
										<i
											className="mr-2 text-purple-500 fas fa-circle"
											style={{ fontSize: "0.5rem" }}
										/>
										Хадгалаагүй өөрчлөлт байна
									</>
								)}
							</p>
							<div className="flex gap-2">
								<button
									type="button"
									className={secondaryButton}
									onClick={() => setForm(saved)}
									disabled={saving}
								>
									Буцаах
								</button>
								<button
									type="button"
									className={
										primaryButton +
										" inline-flex items-center" +
										(canSave ? "" : " opacity-50 cursor-not-allowed")
									}
									onClick={save}
									disabled={!canSave}
								>
									{saving && <i className="mr-2 fas fa-spinner fa-spin" />}
									Хадгалах
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</Admin>
	);
};

Settings.propTypes = {
	getProduct: PropTypes.func.isRequired,
	changePrice: PropTypes.func.isRequired,
	getAlerts: PropTypes.func.isRequired,
	saveAlerts: PropTypes.func.isRequired,
	testAlert: PropTypes.func.isRequired,
	getAds: PropTypes.func.isRequired,
	saveAds: PropTypes.func.isRequired,
	settings: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
	settings: state.settings,
});

export default connect(mapStateToProps, {
	getProduct,
	changePrice,
	getAlerts,
	saveAlerts,
	testAlert,
	getAds,
	saveAds,
})(Settings);
